import { useState, useEffect, useContext } from "react";
import "../styles/application-sidebar.css";
import api from "../services/api-service";
import { useAlert } from "../contexts/AlertContext";
import { AuthContext } from "../contexts/AuthContext";

export default function FilterSidebar({
  isOpen,
  onClose,
  position,
  radius,
  setRadius,
  onFilter,
}) {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  const [type, setType] = useState("");
  const [subtype, setSubtype] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);

  const canChooseStatus =
    user !== null && (user.role === "MODERATOR" || user.role === "ADMIN");

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get("/types");
        setTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch types", err);
      }
    };

    fetchTypes();
  }, []);

  const handleTypeChange = async (typeId) => {
    setType(typeId);
    setSubtype("");

    if (!typeId) {
      setSubtypes([]);
      return;
    }

    try {
      const res = await api.get(`/types/${typeId}/subtypes`);
      setSubtypes(res.data);
    } catch (err) {
      console.error("Failed to fetch subtypes", err);
      setSubtypes([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      showAlert("From date cannot be later than To date.", "error");
      return;
    }

    const params = {};

    if (canChooseStatus && status) {
      params.status = status;
    }

    if (type) {
      params.type_id = Number(type);
    }

    if (subtype) {
      params.subtype_id = Number(subtype);
    }

    if (fromDate) {
      params.from_date = fromDate;
    }

    if (toDate) {
      params.to_date = toDate;
    }

    if (
      position &&
      position.length === 2 &&
      position[0] != null &&
      position[1] != null &&
      radius !== "" &&
      radius != null
    ) {
      params.lat = position[0];
      params.lon = position[1];
      params.radius = Number(radius);
    }

    const endpoint = canChooseStatus
      ? "/incidents/filter-any"
      : "/incidents/filter";

    try {
      const res = await api.get(endpoint, { params });
      if (onFilter) {
        onFilter(res.data);
      }
    } catch (err) {
      console.error("Failed to filter incidents", err);
      showAlert("Failed to apply filters.", "error");
    }

    onClose();
  };

  const handleClear = async () => {
    setStatus("");
    setType("");
    setSubtype("");
    setSubtypes([]);
    setFromDate("");
    setToDate("");
    setRadius("");

    try {
      const endpoint = canChooseStatus
        ? "/incidents/filter-any"
        : "/incidents/approved";

      const res = canChooseStatus
        ? await api.get(endpoint)
        : await api.get(endpoint);

      if (onFilter) {
        onFilter(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch incidents", err);
      showAlert("Failed to clear filters.", "error");
    }
  };

  return (
    <>
      <div
        className={`sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={onClose}
      />

      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h2>Filter Incidents</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form className="incident-form" onSubmit={handleSubmit}>
          {canChooseStatus && (
            <>
              <label>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
              </select>
            </>
          )}

          <label>Type</label>
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="">All Types</option>
            {types?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          {subtypes.length > 0 && (
            <>
              <label>Subtype</label>
              <select
                value={subtype}
                onChange={(e) => setSubtype(e.target.value)}
              >
                <option value="">All Subtypes</option>
                {subtypes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <label>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />

          <label>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />

          <label>Radius {radius ? `(${radius} m)` : ""} </label>
          <input
            type="range"
            min="100"
            max="5000"
            step="100"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />

          <button type="submit" className="submit-btn">
            Apply Filter
          </button>

          <button type="button" className="clear-btn" onClick={handleClear}>
            Clear Filters
          </button>
        </form>
      </div>
    </>
  );
}
